import type { PostHog } from 'posthog-js/dist/module.slim.no-external'
import type { SessionState } from '@stagepost/web/app/useSession'

/**
 * Pageviews on the public site, and nothing else.
 *
 * The signed-in app sends no events at all: usage is counted on the server,
 * once a day per organisation, because per-click events from people who live
 * in this app all day would spend the free tier on noise. The server's half is
 * `cloud/server/stagepost_cloud/`: the milestones the open server records
 * (`server/stagepost_server/milestones.py`) become PostHog events in
 * `__init__.py`, and `usage.py` sends the daily report.
 *
 * Cookieless, so the privacy policy's "one cookie" stays true and there is no
 * banner: PostHog counts unique visitors from a daily-salted hash on its own
 * servers, and the browser stores nothing. That mode only works with
 * cookieless enabled in the PostHog project's settings, which it is.
 *
 * Loaded on first use, so a signed-in tab never downloads it.
 */

const TOKEN = import.meta.env.VITE_POSTHOG_TOKEN

/**
 * Whether this page is one we count.
 *
 * `/shared/` and `/setups/` are public too, but their path is a credential,
 * and an emailed password link is consumed from the URL before anything
 * renders, so neither ever reaches PostHog.
 */
export function isCountedPage(path: string, session: SessionState['status'],
                              passwordLink: boolean): boolean {
  if (passwordLink) return false
  if (path === '/guide' || path.startsWith('/guide/')) return true
  if (path === '/privacy' || path === '/terms') return true
  if (path.startsWith('/shared/') || path.startsWith('/setups/')) return false
  if (path === '/play' || path.startsWith('/play/')) return false
  return session === 'signed-out'
}

let client: Promise<PostHog> | null = null

function load(token: string): Promise<PostHog> {
  return import('posthog-js/dist/module.slim.no-external').then(({ default: posthog }) => {
    posthog.init(token, {
      api_host: 'https://eu.i.posthog.com',
      cookieless_mode: 'always',
      person_profiles: 'never',
      // Every pageview is sent by `countPageview`, so the library sends none
      // of its own and nothing on leaving.
      capture_pageview: false,
      capture_pageleave: false,
      autocapture: false,
      rageclick: false,
      capture_dead_clicks: false,
      capture_heatmaps: false,
      capture_exceptions: false,
      capture_performance: false,
      disable_session_recording: true,
      disable_surveys: true,
      disable_product_tours: true,
      disable_conversations: true,
      disable_web_experiments: true,
      disable_external_dependency_loading: true,
      // The flags request runs on every page load and is billed on its own
      // quota. Nothing here reads a flag.
      advanced_disable_flags: true,
    })
    return posthog
  })
}

export function countPageview(): void {
  if (!TOKEN) return
  client ??= load(TOKEN)
  void client.then((posthog) => posthog.capture('$pageview'))
}
